import { Box, Divider, HorizontalGrid, Text } from '@shopify/polaris';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { VerticalSpace } from '../../../UI/Reports/VerticalSpace';
import { PIE_BLUE, PIE_GREEN, PIE_SALMON, Pie } from './Pie';
import { TableShell } from './TableShell';
import {
  TABLE_BODY_TEXT,
  TABLE_CELL_BORDER,
  TABLE_CELL_PADDING,
  TABLE_DANGER,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_STRONG,
  TABLE_TOTAL_BG,
} from './tableStyles';
import { formatMoney, formatPct, formatUnits } from './formatters';
import TotalizeNumbers from '../../../UI/Reports/TotalizeNumbers';

export const EfficacySummary = ({ metadata: m, rows }) => {
  const { currency } = useCurrency();

  const fmt$ = (n) => formatMoney(n, currency);

  const eficaciaTotal = m.totalBuyUnits > 0 ? m.totalSellUnitsTotal / m.totalBuyUnits : 0;

  const eficaciaIn = m.totalBuyUnits > 0 ? m.totalSellUnitsInSeason / m.totalBuyUnits : 0;
  const rentaIn = m.totalBuyValue > 0 ? m.totalSellValuePvfInSeason / m.totalBuyValue : 0;
  // Dto medio agregado: 1 − PVPm / PVP, donde PVPm = pvp cobrado y PVP =
  // pvp etiqueta (sumado al nivel de uds vendidas). Si falta el PVP
  // etiqueta agregado para ese tramo, devolvemos null en vez de 0 para no
  // mostrar "0%" engañoso.
  const dtoIn = m.totalPvpListedInSeason > 0 ? 1 - m.totalSellValuePvpInSeason / m.totalPvpListedInSeason : null;

  const eficaciaOut = m.totalBuyUnits > 0 ? m.totalSellUnitsOutSeason / m.totalBuyUnits : 0;
  const rentaOut = m.totalBuyValue > 0 ? m.totalSellValuePvfOutSeason / m.totalBuyValue : 0;
  const dtoOut = m.totalPvpListedOutSeason > 0 ? 1 - m.totalSellValuePvpOutSeason / m.totalPvpListedOutSeason : null;

  // El radar "Distribución del almacén" vive ahora en `WarehouseRadar`,
  // renderizado en su propia Card "Análisis del stock restante"
  // junto a los escenarios de liquidación (ver ViewReportEfficacy).

  // Tabla "Resultado contable" — vista pseudo-balance:
  //   filas: Compras (outflow) → Ventas campaña + fuera → Ventas
  //          totales (subtotal) → TOTAL (= Ventas - Compras).
  //   columnas: Unidades, Eficacia, Valor PVP, Valor PVF, PVPm,
  //             Rentabilidad.
  //   notación contable: negativos y compras (outflow) van entre
  //                      paréntesis. Negativos también en rojo.
  const totalUnitsResult = m.totalSellUnitsTotal - m.totalBuyUnits;
  const totalPvfResult = m.totalSellValuePvfTotal - m.totalBuyValue;
  const pvpmIn = m.totalSellUnitsInSeason > 0 ? m.totalSellValuePvpInSeason / m.totalSellUnitsInSeason : null;
  const pvpmOut = m.totalSellUnitsOutSeason > 0 ? m.totalSellValuePvpOutSeason / m.totalSellUnitsOutSeason : null;
  const pvpmTotal = m.totalSellUnitsTotal > 0 ? m.totalSellValuePvpTotal / m.totalSellUnitsTotal : null;
  const rentaTotal = m.totalBuyValue > 0 ? m.totalSellValuePvfTotal / m.totalBuyValue : null;
  const margenNeto = m.totalBuyValue > 0 ? totalPvfResult / m.totalBuyValue : null;
  // Dto. medio agregado sobre las dos ventanas: 1 − Σ PVP cobrado / Σ
  // PVP etiqueta. Para las "Ventas totales" sumamos las dos referencias.
  const totalPvpListed = (Number(m.totalPvpListedInSeason) || 0) + (Number(m.totalPvpListedOutSeason) || 0);
  const dtoTotal = totalPvpListed > 0 ? 1 - m.totalSellValuePvpTotal / totalPvpListed : null;

  // PVP medio de la COLECCIÓN comprada (no del lote vendido) — el
  // back lo expone como `totalPvpListedBuy` = Σ pvpEtiqueta × uds
  // compradas por familia. Si por alguna razón no viene, fallback a
  // null para no mostrar el `cu` (coste medio) que confundía con un
  // PVP medio.
  const pvpmCompras =
    Number(m.totalPvpListedBuy) > 0 && m.totalBuyUnits > 0 ? m.totalPvpListedBuy / m.totalBuyUnits : null;

  // Helpers de formato — `paren` fuerza paréntesis aunque el valor
  // sea positivo (caso Compras, que aquí se cuenta como outflow).
  const fmtAcc = (n, formatter, { paren = false } = {}) => {
    if (n == null || !Number.isFinite(n)) return '—';
    if (paren) return `(${formatter(Math.abs(n))})`;
    return n < 0 ? `(${formatter(Math.abs(n))})` : formatter(n);
  };
  const accountingRows = [
    {
      key: 'compras',
      label: 'Compras',
      paren: true,
      units: m.totalBuyUnits,
      eficacia: null,
      pvp: null,
      pvf: m.totalBuyValue,
      pvpm: pvpmCompras,
      dtom: null,
      renta: null,
    },
    {
      key: 'in',
      label: 'Ventas en campaña',
      units: m.totalSellUnitsInSeason,
      eficacia: eficaciaIn,
      pvp: m.totalSellValuePvpInSeason,
      pvf: m.totalSellValuePvfInSeason,
      pvpm: pvpmIn,
      dtom: dtoIn,
      renta: rentaIn,
    },
    {
      key: 'out',
      label: 'Ventas fuera de campaña',
      units: m.totalSellUnitsOutSeason,
      eficacia: eficaciaOut,
      pvp: m.totalSellValuePvpOutSeason,
      pvf: m.totalSellValuePvfOutSeason,
      pvpm: pvpmOut,
      dtom: dtoOut,
      renta: rentaOut,
    },
    {
      key: 'ventas',
      label: 'Ventas totales',
      subtotal: true,
      units: m.totalSellUnitsTotal,
      eficacia: eficaciaTotal,
      pvp: m.totalSellValuePvpTotal,
      pvf: m.totalSellValuePvfTotal,
      pvpm: pvpmTotal,
      dtom: dtoTotal,
      renta: rentaTotal,
    },
  ];

  const accountingTotalRow = {
    label: 'Total',
    units: totalUnitsResult,
    pvf: totalPvfResult,
    renta: margenNeto,
  };

  const acTh = {
    ...TABLE_CELL_PADDING,
    backgroundColor: TABLE_HEADER_BG,
    color: TABLE_HEADER_TEXT_STRONG,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderBottom: TABLE_CELL_BORDER,
  };
  const acTd = {
    ...TABLE_CELL_PADDING,
    color: TABLE_BODY_TEXT,
    borderBottom: TABLE_CELL_BORDER,
    fontSize: 13,
    whiteSpace: 'nowrap',
  };
  // Renderiza una celda numérica con el valor formateado y el color
  // según signo. `paren=true` fuerza paréntesis (caso Compras).
  const accountingCell = (raw, formatter, { paren = false, bold = false } = {}) => {
    if (raw == null || !Number.isFinite(raw)) return <td style={{ ...acTd, textAlign: 'right' }}>—</td>;
    const neg = !paren && raw < 0;
    return (
      <td
        style={{
          ...acTd,
          textAlign: 'right',
          fontWeight: bold || neg ? 700 : 400,
          color: neg ? TABLE_DANGER : TABLE_BODY_TEXT,
        }}
      >
        {fmtAcc(raw, formatter, { paren })}
      </td>
    );
  };

  return (
    <>
      {/* Pasteles arriba del todo: dan vista de pájaro instantánea.
          Antes en Card propio; ahora vivimos dentro del Card del tab. */}
      <Box>
        <Text variant="headingMd" as="h3">
          Distribución de ventas
        </Text>
        <Box minHeight="1.5rem" />
        <TotalizeNumbers
          rows={[
            {
              label: 'Inversión',
              value: `${fmt$(m.totalBuyValue)}`,
            },
            {
              label: 'Rentabilidad total',
              value:
                m.totalBuyValue > 0 ? (
                  `${formatPct(rentaTotal - 1)}`
                ) : (
                  <span style={{ color: '#5C5F62', fontStyle: 'italic' }}>N/A</span>
                ),
            },
            {
              label: 'Uds. Compradas',
              value: `${formatUnits(m.totalBuyUnits)} uds.`,
            },
            { label: 'Uds. vendidas', value: `${formatUnits(m.totalSellUnitsTotal)} uds.` },
            {
              label: '% vendidas',
              value:
                m.totalBuyUnits > 0 ? (
                  `${formatPct(m.totalSellUnitsTotal / m.totalBuyUnits)}`
                ) : (
                  <span style={{ color: '#5C5F62', fontStyle: 'italic' }}>N/A</span>
                ),
            },
          ]}
        />
        <Box minHeight="1rem" />
        <Divider />
        <VerticalSpace />
        <Box paddingBlockStart={8} paddingInlineStart={20}>
          <HorizontalGrid columns={{ xs: 1, md: 2 }} gap="6">
            <Pie
              title="Unidades"
              slices={[
                { label: 'Venta en campaña', value: Math.max(0, m.totalSellUnitsInSeason), color: PIE_BLUE },
                { label: 'Venta fuera de campaña', value: Math.max(0, m.totalSellUnitsOutSeason), color: PIE_SALMON },
                { label: 'En almacén', value: Math.max(0, m.totalRemainUnits), color: PIE_GREEN },
              ]}
              formatValue={(v) => `${formatUnits(v)} uds`}
            />
            <Pie
              title="Valor ventas y almacén (PVF)"
              slices={[
                { label: 'Venta en campaña', value: Math.max(0, m.totalSellValuePvfInSeason), color: PIE_BLUE },
                {
                  label: 'Venta fuera de campaña',
                  value: Math.max(0, m.totalSellValuePvfOutSeason),
                  color: PIE_SALMON,
                },
                { label: 'Valor Almacén', value: Math.max(0, m.totalRemainValueCI), color: PIE_GREEN },
              ]}
              formatValue={fmt$}
            />
          </HorizontalGrid>
        </Box>{' '}
        <Box minHeight="2rem" />
        <Text variant="headingMd" as="h3">
          Resultado contable
        </Text>
        <Box minHeight=".25rem" />
        <Text variant="bodySm" tone="subdued">
          Valores de resta entre paréntesis. <br />
          Cifras negativas también en rojo y entre paréntesis.
        </Text>
        <VerticalSpace />
        <TableShell>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {[
                    { label: 'Concepto', align: 'left' },
                    { label: 'Unidades', align: 'right' },
                    { label: 'Eficacia', align: 'right' },
                    { label: 'Valor PVP', align: 'right' },
                    { label: 'Valor PVF', align: 'right' },
                    // `mean: true` pinta una barrita encima (notación
                    // estadística para promedio).
                    { label: 'PVP', align: 'right', mean: true },
                    { label: 'Dto.', align: 'right', mean: true },
                    { label: 'Rentabilidad', align: 'right' },
                  ].map((c) => (
                    <th key={c.label} style={{ ...acTh, textAlign: c.align }}>
                      {c.mean ? (
                        <span style={{ borderTop: '1px solid currentColor', paddingTop: 1, display: 'inline-block' }}>
                          {c.label}
                        </span>
                      ) : (
                        c.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accountingRows.map((r) => {
                  const subBold = !!r.subtotal;
                  return (
                    <tr key={r.key} className="efftable-row">
                      <td
                        style={{
                          ...acTd,
                          fontWeight: r.subtotal ? 700 : r.paren ? 600 : 500,
                          color: TABLE_BODY_TEXT,
                        }}
                      >
                        {r.label}
                      </td>
                      {accountingCell(r.units, formatUnits, { paren: r.paren, bold: subBold })}
                      {r.eficacia == null ? (
                        <td style={{ ...acTd, textAlign: 'right' }}>—</td>
                      ) : (
                        accountingCell(r.eficacia, formatPct, { bold: subBold })
                      )}
                      {r.pvp == null ? (
                        <td style={{ ...acTd, textAlign: 'right' }}>—</td>
                      ) : (
                        accountingCell(r.pvp, fmt$, { paren: r.paren, bold: subBold })
                      )}
                      {accountingCell(r.pvf, fmt$, { paren: r.paren, bold: subBold })}
                      {r.pvpm == null ? (
                        <td style={{ ...acTd, textAlign: 'right' }}>—</td>
                      ) : (
                        accountingCell(r.pvpm, fmt$, { bold: subBold })
                      )}
                      {r.dtom == null ? (
                        <td style={{ ...acTd, textAlign: 'right' }}>—</td>
                      ) : (
                        accountingCell(r.dtom, formatPct, { bold: subBold })
                      )}
                      {r.renta == null ? (
                        <td style={{ ...acTd, textAlign: 'right' }}>—</td>
                      ) : (
                        accountingCell(r.renta, formatPct, { bold: subBold })
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: TABLE_TOTAL_BG }}>
                  <td
                    style={{
                      ...acTd,
                      fontWeight: 800,
                      color: TABLE_BODY_TEXT,
                      borderTop: `2px solid ${'#E1E3E5'}`,
                      borderBottom: 'none',
                    }}
                  >
                    {accountingTotalRow.label}
                  </td>
                  {accountingCell(accountingTotalRow.units, formatUnits, { bold: true })}
                  <td
                    style={{
                      ...acTd,
                      textAlign: 'right',
                      borderTop: `2px solid ${'#E1E3E5'}`,
                      borderBottom: 'none',
                    }}
                  >
                    —
                  </td>
                  <td
                    style={{
                      ...acTd,
                      textAlign: 'right',
                      borderTop: `2px solid ${'#E1E3E5'}`,
                      borderBottom: 'none',
                    }}
                  >
                    —
                  </td>
                  {accountingCell(accountingTotalRow.pvf, fmt$, { bold: true })}
                  <td
                    style={{
                      ...acTd,
                      textAlign: 'right',
                      borderTop: `2px solid ${'#E1E3E5'}`,
                      borderBottom: 'none',
                    }}
                  >
                    —
                  </td>
                  <td
                    style={{
                      ...acTd,
                      textAlign: 'right',
                      borderTop: `2px solid ${'#E1E3E5'}`,
                      borderBottom: 'none',
                    }}
                  >
                    —
                  </td>
                  {accountingCell(accountingTotalRow.renta, formatPct, { bold: true })}
                </tr>
              </tfoot>
            </table>
          </div>
        </TableShell>
        <VerticalSpace />
      </Box>
    </>
  );
};
